import { useState, } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { X, Search, Rocket, Bell, Star, Check, AlertCircle } from "lucide-react";
import {
  boostCreator, setDropAlert, filterComment
} from "../services/boostService";
import { spendCredits } from "../services/userPerksService";

const ITEM_COSTS = {
  boost_creator:   100,
  drop_alert:       50,
  super_comment:    50,
};

/* ── CREATOR SEARCH ── */
function CreatorSearch({ onSelect, selected }) {
  const [query2, setQuery2] = useState("");
  const [results, setResults] = useState([]);
  const [setSearching] = useState(false);

  const search = async (val) => {
    setQuery2(val);
    if (val.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const snap = await getDocs(collection(db, "users"));
      const lower = val.toLowerCase();
      setResults(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(u => u.isCreator &&
            (u.username?.toLowerCase().includes(lower) ||
             u.displayName?.toLowerCase().includes(lower)))
          .slice(0, 6)
      );
    } finally { setSearching(false); }
  };

  if (selected) return (
    <div className="uio-selected-creator">
      <img src={selected.avatar || "https://i.pravatar.cc/100"} alt={selected.displayName} />
      <div>
        <strong>{selected.displayName}</strong>
        <span>@{selected.username}</span>
      </div>
      <button type="button" className="uio-clear-btn" onClick={() => onSelect(null)}>
        <X size={14} strokeWidth={2.5} />
      </button>
    </div>
  );

  return (
    <div className="uio-search-wrap">
      <div className="uio-search-input-wrap">
        <Search size={15} color="var(--muted)" strokeWidth={2} />
        <input
          type="text"
          placeholder="Search creators..."
          value={query2}
          onChange={e => search(e.target.value)}
          className="uio-search-input"
          autoFocus
        />
      </div>
      {results.length > 0 && (
        <div className="uio-search-results">
          {results.map(u => (
            <button key={u.id} type="button" className="uio-result-row" onClick={() => onSelect(u)}>
              <img src={u.avatar || "https://i.pravatar.cc/100"} alt={u.displayName} />
              <div>
                <strong>{u.displayName}</strong>
                <span>@{u.username}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── BOOST OVERLAY ── */
function BoostOverlay({ onClose, onSuccess, preselectedCreator }) {
  const { currentUser, refreshUserPerks } = useAuth();
  const [creator, setCreator] = useState(preselectedCreator || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleBoost = async () => {
    if (!creator) return;
    setLoading(true);
    setError(null);
    try {
      const spend = await spendCredits(currentUser.uid, ITEM_COSTS.boost_creator, "boost_creator", { description: "Creator Boost" });
      if (!spend.success) { setError(spend.error || "Not enough credits"); setLoading(false); return; }
      const result = await boostCreator(currentUser.uid, creator.id);
      if (!result.success) { setError(result.error); setLoading(false); return; }
      await refreshUserPerks(currentUser.uid);
      onSuccess(`🚀 ${creator.displayName} is now boosted for 24 hours!`);
    } catch (err) {
      setError("Something went wrong. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="uio-content">
      <div className="uio-icon-header" style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}>
        <Rocket size={28} color="white" strokeWidth={1.8} />
      </div>
      <h2>Boost a Creator</h2>
      <p>Their profile gets highlighted in Discover for 24 hours, sending them more viewers.</p>
      <div className="uio-cost-pill"><span>C</span> 100 credits</div>
      <div className="uio-section-label">Who do you want to boost?</div>
      <CreatorSearch onSelect={setCreator} selected={creator} />
      {error && <div className="uio-error"><AlertCircle size={14} strokeWidth={2} />{error}</div>}
      <button
        type="button"
        className={`uio-confirm-btn ${!creator || loading ? "disabled" : ""}`}
        onClick={handleBoost}
        disabled={!creator || loading}
      >
        {loading ? "Boosting..." : creator ? `🚀 Boost ${creator.displayName}` : "Select a creator first"}
      </button>
    </div>
  );
}

/* ── DROP ALERT OVERLAY ── */
function DropAlertOverlay({ onClose, onSuccess, preselectedCreator }) {
  const { currentUser, refreshUserPerks } = useAuth();
  const [creator, setCreator] = useState(preselectedCreator || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAlert = async () => {
    if (!creator) return;
    setLoading(true);
    setError(null);
    try {
      const spend = await spendCredits(currentUser.uid, ITEM_COSTS.drop_alert, "drop_alert", { description: "Drop Alert" });
      if (!spend.success) { setError(spend.error || "Not enough credits"); setLoading(false); return; }
      const result = await setDropAlert(currentUser.uid, creator.id);
      if (!result.success) { setError(result.error); setLoading(false); return; }
      await refreshUserPerks(currentUser.uid);
      onSuccess(`🔔 You'll be alerted when ${creator.displayName} drops something new!`);
    } catch (err) {
      setError("Something went wrong. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="uio-content">
      <div className="uio-icon-header" style={{ background: "linear-gradient(135deg,#f59e0b,#d97706)" }}>
        <Bell size={28} color="white" strokeWidth={1.8} />
      </div>
      <h2>Set a Drop Alert</h2>
      <p>Get notified the moment this creator drops new content — before anyone else sees it.</p>
      <div className="uio-cost-pill"><span>C</span> 50 credits</div>
      <div className="uio-section-label">Which creator do you want alerts for?</div>
      <CreatorSearch onSelect={setCreator} selected={creator} />
      {error && <div className="uio-error"><AlertCircle size={14} strokeWidth={2} />{error}</div>}
      <button
        type="button"
        className={`uio-confirm-btn ${!creator || loading ? "disabled" : ""}`}
        onClick={handleAlert}
        disabled={!creator || loading}
      >
        {loading ? "Setting alert..." : creator ? `🔔 Alert me for ${creator.displayName}` : "Select a creator first"}
      </button>
    </div>
  );
}

/* ── SUPER COMMENT OVERLAY ── */
function SuperCommentOverlay({ onClose, onSuccess }) {
  const { currentUser, refreshUserPerks } = useAuth();
  const [text,] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const filter = filterComment(text);

  const handlePost = async () => {
    if (!filter.ok) { setError(filter.reason); return; }
    setLoading(true);
    setError(null);
    try {
      const spend = await spendCredits(currentUser.uid, ITEM_COSTS.super_comment, "super_comment", { description: "Super Comment" });
      if (!spend.success) { setError(spend.error || "Not enough credits"); setLoading(false); return; }
      // Store pending super comment in userPerks so it activates on next comment
      const perksRef = doc(db, "userPerks", currentUser.uid);
      await updateDoc(perksRef, { pendingSuperComment: true });
      await refreshUserPerks(currentUser.uid);
      onSuccess("⭐ Your next comment will be pinned as a Super Comment!");
    } catch (err) {
      setError("Something went wrong. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="uio-content">
      <div className="uio-icon-header" style={{ background: "linear-gradient(135deg,#f97316,#ea580c)" }}>
        <Star size={28} color="white" strokeWidth={1.8} />
      </div>
      <h2>Super Comment</h2>
      <p>Your next comment on any moment will be pinned to the top with a gold star badge for 24 hours.</p>
      <div className="uio-cost-pill"><span>C</span> 50 credits</div>
      <div className="uio-super-notice">
        <Star size={13} color="#f59e0b" strokeWidth={2.5} />
        After activating, your next comment on any moment will be pinned. The creator can remove it if needed and you'll get a full refund.
      </div>
      {error && <div className="uio-error"><AlertCircle size={14} strokeWidth={2} />{error}</div>}
      <button
        type="button"
        className={`uio-confirm-btn ${loading ? "disabled" : ""}`}
        onClick={handlePost}
        disabled={loading}
      >
        {loading ? "Activating..." : "⭐ Activate Super Comment"}
      </button>
    </div>
  );
}

/* ── COMING SOON OVERLAY ── */
function ComingSoonOverlay({ itemName }) {
  return (
    <div className="uio-content uio-coming-soon">
      <div className="uio-coming-soon-icon">🚧</div>
      <h2>{itemName}</h2>
      <p>This feature is coming soon. We're working on it and it'll be ready before you know it.</p>
      <div className="uio-coming-badge">Coming soon</div>
    </div>
  );
}

/* ── MAIN EXPORT ── */
export default function UseItemOverlay({ itemId, itemName, onClose, preselectedCreator }) {
  const [success, setSuccess] = useState(null);

  const handleSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => { onClose(); }, 2200);
  };

  const renderContent = () => {
    if (success) return (
      <div className="uio-content uio-success">
        <div className="uio-success-icon"><Check size={32} color="white" strokeWidth={2.5} /></div>
        <p>{success}</p>
      </div>
    );
    switch (itemId) {
      case "boost_creator":   return <BoostOverlay onClose={onClose} onSuccess={handleSuccess} preselectedCreator={preselectedCreator} />;
      case "drop_alert":      return <DropAlertOverlay onClose={onClose} onSuccess={handleSuccess} preselectedCreator={preselectedCreator} />;
      case "super_comment":   return <SuperCommentOverlay onClose={onClose} onSuccess={handleSuccess} />;
      case "credit_tip_small":
      case "credit_tip_large":return <ComingSoonOverlay itemName={itemName || "Credit Tips"} />;
      default:                return <ComingSoonOverlay itemName={itemName || itemId} />;
    }
  };

  return (
    <div className="uio-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="uio-sheet">
        <button type="button" className="uio-close" onClick={onClose}>
          <X size={18} strokeWidth={2.5} />
        </button>
        {renderContent()}
      </div>
    </div>
  );
}